import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createMRTColumnHelper } from 'material-react-table';
import MRTTableAdapter from './MRTTableAdapter';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface MyTaskRow {
  id: number | string;
  title: string;
  description?: string;
  task_type: string;
  priority: string;
  status: string;
  due_date?: string | null;
  created_at: string;
  related_entity_type?: string | null;
  related_entity_id?: number | null;
  source?: 'workflow' | 'general' | 'derived_sae' | string;
  workflow_id?: number | null;
}

const MyTasksMRT: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<MyTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  // Toolbar quick filters
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'workflow' | 'general' | 'derived_sae'>('all');
  const [showOpenOnly, setShowOpenOnly] = useState(true);
  const fetchTasks = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      // Combined tasks (general + workflow) for current user/company
      const res = await fetch(`${API_BASE}/my-tasks/`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`Failed to fetch tasks (${res.status})`);
      const payload = await res.json();
      const data = Array.isArray(payload) ? payload : (payload?.data || []);
      // Minimal normalization, keep backend fields
      setRows(data.map((r: any) => ({
        id: r.id,
        title: r.title,
        description: r.description,
        task_type: r.task_type,
        priority: r.priority,
        status: r.status,
        due_date: r.due_date,
        created_at: r.created_at,
        source: r.source || (r.workflow_id ? 'workflow' : 'general'),
        related_entity_type: r.related_entity_type,
        related_entity_id: r.related_entity_id,
        workflow_id: r.workflow_id ?? null,
      })));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const columnHelper = createMRTColumnHelper<MyTaskRow>();
  const isOverdue = useCallback((row: MyTaskRow) => {
    if (!row.due_date) return false;
    const due = new Date(row.due_date).getTime();
    const now = Date.now();
    return (row.status === 'pending' || row.status === 'in_progress') && due < now;
  }, []);

  const displayedRows = useMemo(() => {
    return rows.filter((r) => {
      if (sourceFilter !== 'all' && (r.source || 'general') !== sourceFilter) return false;
      if (showOpenOnly && !(r.status === 'pending' || r.status === 'in_progress')) return false;
      if (showOverdueOnly && !isOverdue(r)) return false;
      return true;
    });
  }, [rows, sourceFilter, showOpenOnly, showOverdueOnly, isOverdue]);

  const stats = useMemo(() => {
    const total = rows.length;
    const open = rows.filter(r => r.status === 'pending' || r.status === 'in_progress').length;
    const overdue = rows.filter(r => isOverdue(r)).length;
    return { total, open, overdue };
  }, [rows, isOverdue]);

  const columns = useMemo(() => {
    const chip = (cls: string, label: string) => (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>
    );
    const statusChip = (s: string) => {
      const v = (s || '').toLowerCase();
      if (v === 'in_progress') return chip('bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/50', 'In Progress');
      if (v === 'completed') return chip('bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50', 'Completed');
      if (v === 'cancelled') return chip('bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:border-slate-700', 'Cancelled');
      return chip('bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50', 'Pending');
    };

  // Create an AE submission directly from a task row
  const createSubmissionForAE = async (aeId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !aeId) return;
      const res = await fetch(`${API_BASE}/submissions/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ submission_type: 'adverse_event', linked_record_id: aeId })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Failed to create submission');
      }
      setSuccess('Created AE submission');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err?.message || String(err));
      setTimeout(() => setError(''), 3000);
    }
  };
    const priorityChip = (p: string) => {
      const v = (p || '').toLowerCase();
      if (v === 'high') return chip('bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700/50', 'High');
      if (v === 'low') return chip('bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:border-slate-700', 'Low');
      return chip('bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:border-slate-700', 'Normal');
    };
    const sourceChip = (src?: string) => {
      if (src === 'workflow') return chip('bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700/50', 'Workflow');
      if (src === 'derived_sae') return chip('bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700/50', 'SAE Deadline');
      return chip('bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700/50', 'General');
    };
    const dueChip = (d?: string | null) => {
      if (!d) return chip('bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700', '—');
      const date = new Date(d);
      const days = Math.ceil((date.getTime() - Date.now()) / (1000*60*60*24));
      const label = date.toISOString().slice(0,10);
      if (days < 0) return chip('bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700/50', label);
      if (days <= 2) return chip('bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50', label);
      return chip('bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/50 dark:text-slate-200 dark:border-slate-700', label);
    };

    return [
      columnHelper.accessor('id', { header: 'Task ID', size: 100 }),
      columnHelper.accessor(row => (row.source === 'workflow' ? 'Workflow' : (row.source === 'derived_sae' ? 'SAE Deadline' : 'General')), { id: 'source', header: 'Source', size: 130, filterVariant: 'select',
        Cell: ({ row }) => sourceChip(row.original.source) }),
      columnHelper.accessor('title', { header: 'Title', size: 260 }),
      columnHelper.accessor('task_type', { header: 'Type', size: 140, filterVariant: 'select' }),
      columnHelper.accessor('priority', { header: 'Priority', size: 110, filterVariant: 'select',
        Cell: ({ cell }) => priorityChip(String(cell.getValue() || '')) }),
      columnHelper.accessor('status', { header: 'Status', size: 130, filterVariant: 'select',
        Cell: ({ cell }) => statusChip(String(cell.getValue() || '')) }),
      columnHelper.accessor(row => {
        if (row.related_entity_type === 'adverse_event') return `AE #${row.related_entity_id}`;
        if (row.related_entity_type && row.related_entity_id) return `${row.related_entity_type} #${row.related_entity_id}`;
        return '';
      }, { id: 'related', header: 'Related', size: 140 }),
      columnHelper.accessor(row => (row.due_date ? new Date(row.due_date).toISOString().slice(0,10) : ''), { id: 'due_date', header: 'Due Date', size: 140, filterVariant: 'date-range',
        Cell: ({ row }) => dueChip(row.original.due_date) }),
      columnHelper.accessor(row => (row.created_at ? new Date(row.created_at).toISOString().slice(0,10) : ''), { id: 'created_at', header: 'Created', size: 120, filterVariant: 'date-range' }),
      columnHelper.display({
        id: 'overdue',
        header: 'Overdue',
        size: 90,
        Cell: ({ row }) => (
          isOverdue(row.original)
            ? <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">Yes</span>
            : <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600">No</span>
        )
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 280,
        Cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            {row.original.source === 'derived_sae' ? (
              <>
                <span className="px-2.5 py-1.5 text-xs rounded-md border border-rose-300 text-rose-700 bg-rose-50" title="This is a derived SAE deadline task shown for MoCRA compliance">Derived</span>
                <button
                  onClick={async (e) => { e.stopPropagation(); await refreshOne(row.original); }}
                  className="px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                  title="Refresh"
                >Refresh</button>
              </>
            ) : (
              <>
                <button
                  onClick={async (e) => { e.stopPropagation(); await handleStatusUpdate(row.original, 'in_progress'); }}
                  className="px-2.5 py-1.5 text-xs rounded-md border border-blue-300 text-blue-700 bg-white hover:bg-blue-50"
                  title="Start"
                >Start</button>
                <button
                  onClick={async (e) => { e.stopPropagation(); await handleStatusUpdate(row.original, 'completed'); }}
                  className="px-2.5 py-1.5 text-xs rounded-md border border-green-300 text-green-700 bg-white hover:bg-green-50"
                  title="Mark Complete"
                >Complete</button>
                <button
                  onClick={async (e) => { e.stopPropagation(); await refreshOne(row.original); }}
                  className="px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                  title="Refresh"
                >Refresh</button>
                {row.original.related_entity_type === 'adverse_event' && row.original.related_entity_id ? (
                  <button
                    onClick={async (e) => { e.stopPropagation(); await createSubmissionForAE(row.original.related_entity_id as number); }}
                    className="px-2.5 py-1.5 text-xs rounded-md border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    title="Create Submission for this AE"
                  >Create Submission</button>
                ) : null}
              </>
            )}
          </div>
        ),
      }),
    ];
  }, [columnHelper, isOverdue]);

  const refreshOne = async (row: MyTaskRow) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      if (row.source === 'workflow') {
        const res = await fetch(`${API_BASE}/workflow/tasks/${Number(row.id)}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) return;
        const detail = await res.json();
        setRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...detail } : r)));
      } else {
        // For general tasks, re-fetch all for simplicity
        await fetchTasks();
      }
    } catch {}
  };

  const handleBulkUpdate = async (selected: MyTaskRow[], status: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } as any;
      const requests = selected.map((row) => {
        if (row.source === 'workflow') {
          return fetch(`${API_BASE}/workflow/tasks/${row.id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
        }
        return fetch(`${API_BASE}/tasks/${row.id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
      });
      const results = await Promise.all(requests);
      const okCount = results.filter(r => r.ok).length;
      await fetchTasks();
      setSuccess(`Updated ${okCount} of ${selected.length} tasks`);
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err?.message || String(err));
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleStatusUpdate = async (row: MyTaskRow, status: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      let res: Response | null = null;
      if (row.source === 'workflow') {
        res = await fetch(`${API_BASE}/workflow/tasks/${Number(row.id)}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status })
        });
      } else {
        res = await fetch(`${API_BASE}/tasks/${row.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status })
        });
      }
      if (!res?.ok) {
        const txt = await res?.text();
        throw new Error((txt as string) || `Failed`);
      }
      await fetchTasks();
      setSuccess('Task status updated');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err: any) {
      setError(err?.message || String(err));
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading my tasks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="rounded-xl overflow-hidden shadow-sm border border-slate-200">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold">My Tasks</h3>
              <p className="text-xs opacity-90">Assigned to you or currently unassigned • Company scoped</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <div className="px-3 py-1 rounded-full bg-white/15 text-white text-xs">Workflow + General</div>
          </div>
        </div>
        <div className="bg-white p-5 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 text-sm text-slate-700 space-y-3">
            <p className="leading-5">This page shows two kinds of items:</p>
            <ul className="space-y-1">
              <li className="flex items-start gap-2"><span className="mt-1 w-2 h-2 rounded-full bg-indigo-500"></span><span><span className="font-medium">Workflow Tasks</span> generated by adverse event workflows (<span className="font-mono">Source = Workflow</span>).</span></li>
              <li className="flex items-start gap-2"><span className="mt-1 w-2 h-2 rounded-full bg-blue-500"></span><span><span className="font-medium">General Tasks</span> for compliance/operations (<span className="font-mono">Source = General</span>).</span></li>
            </ul>
            <p className="leading-5">Sorted by soonest <span className="font-medium">Due Date</span> (items without a due date appear last), then newest created. Up to the top 50 combined items are shown.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-slate-900 font-medium mb-1">Working with tasks</div>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• Use Open/Overdue and Source filters to focus.</li>
                  <li>• Start / Complete updates status via the correct backend:</li>
                  <li className="ml-3">• Workflow → <span className="font-mono">PUT /workflow/tasks/{`{id}`}</span></li>
                  <li className="ml-3">• General → <span className="font-mono">PUT /tasks/{`{id}`}</span></li>
                </ul>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-slate-900 font-medium mb-1">Visibility</div>
                <p className="text-xs text-slate-700">Shows tasks assigned to you and items currently unassigned within your company. Overdue = due date in the past while status is pending/in_progress.</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-1 grid grid-cols-3 md:grid-cols-1 gap-3">
            <div className="rounded-lg border border-slate-200 p-3 text-center">
              <div className="text-xs text-slate-500">Total</div>
              <div className="text-2xl font-semibold text-slate-900">{stats.total}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 text-center">
              <div className="text-xs text-slate-500">Open</div>
              <div className="text-2xl font-semibold text-indigo-700">{stats.open}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 text-center">
              <div className="text-xs text-slate-500">Overdue</div>
              <div className="text-2xl font-semibold text-rose-600">{stats.overdue}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* MRT Table */}
      <MRTTableAdapter<MyTaskRow>
        columns={columns as any}
        data={displayedRows}
        enableRowSelection={true}
        enableColumnFilters={true}
        enableGlobalFilter={true}
        enablePagination={true}
        initialPageSize={50}
        rowsPerPageOptions={[10, 25, 50, 100]}
        extraTopToolbarActions={({ table }) => (
          <div className="flex items-center gap-3 text-slate-800 dark:text-slate-200">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4 text-indigo-600" checked={showOpenOnly} onChange={(e) => setShowOpenOnly(e.target.checked)} />
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 dark:text-slate-100 border border-slate-300 dark:border-slate-600">Open only</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4 text-indigo-600" checked={showOverdueOnly} onChange={(e) => setShowOverdueOnly(e.target.checked)} />
              <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 dark:text-slate-100 border border-slate-300 dark:border-slate-600">Overdue only</span>
            </label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as any)}
              className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm bg-white dark:bg-slate-800 dark:text-slate-100"
              title="Source filter"
            >
              <option value="all">All Sources</option>
              <option value="workflow">Workflow</option>
              <option value="general">General</option>
              <option value="derived_sae">SAE Deadline</option>
            </select>
            <button
              onClick={async () => {
                const selected = table.getSelectedRowModel().rows.map((r: any) => r.original as MyTaskRow);
                if (selected.length === 0) return;
                await handleBulkUpdate(selected, 'in_progress');
                table.resetRowSelection();
              }}
              className="px-3 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm bg-white"
              title="Start selected"
            >Start Selected</button>
            <button
              onClick={async () => {
                const selected = table.getSelectedRowModel().rows.map((r: any) => r.original as MyTaskRow);
                if (selected.length === 0) return;
                await handleBulkUpdate(selected, 'completed');
                table.resetRowSelection();
              }}
              className="px-3 py-1 rounded border border-green-300 text-green-700 hover:bg-green-50 text-sm bg-white"
              title="Complete selected"
            >Complete Selected</button>
            <button
              onClick={() => fetchTasks()}
              className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-sm bg-white"
              title="Refresh"
            >Refresh</button>
          </div>
        )}
      />
    </div>
  );
};

export default MyTasksMRT;
